import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { mondayOf, weekLabel } from "./weekUtils";
import { addPendingIrrigationLog } from "../../lib/offline";
import { useI18n } from "../../lib/i18n";
import { Droplets, ChevronLeft, ChevronRight, CloudOff } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export default function IrrigatorLog() {
  const { t } = useI18n();
  const [week, setWeek] = useState(mondayOf());
  const [rows, setRows] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const [f, setF] = useState({ date: today(), hours: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = async (w) => {
    try {
      const { data } = await api.get(`/irrigation/schedule?week=${w}`);
      setRows(data.rows);
    } catch { setRows([]); }
  };
  useEffect(() => { load(week); }, [week]); // eslint-disable-line

  const shift = (n) => {
    const d = new Date(week + "T12:00:00"); d.setDate(d.getDate() + n * 7);
    setWeek(mondayOf(d));
  };

  const openLog = (r) => { setLogFor(r); setF({ date: today(), hours: "", note: "" }); };

  const submit = async () => {
    const payload = { block_id: logFor.block_id, date: f.date, hours: Number(f.hours), note: f.note || null };
    if (!payload.hours || payload.hours <= 0) { toast.error(t("il.enter_hours")); return; }
    setBusy(true);
    try {
      if (navigator.onLine) {
        await api.post("/irrigation/logs", payload);
        toast.success(t("il.logged"));
      } else {
        throw new Error("offline");
      }
    } catch (e) {
      await addPendingIrrigationLog({ id: crypto.randomUUID(), payload });
      toast.info(t("il.queued"));
    } finally {
      setBusy(false);
      setLogFor(null);
      load(week);
    }
  };

  if (!rows) return <div className="p-8 font-bold uppercase text-sm">{t("il.loading")}</div>;

  return (
    <div className="space-y-5" data-testid="irrigator-log-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>{t("il.title")}</h1>
        <div className="flex items-center gap-3 mt-2">
          <button data-testid="ilog-week-prev" onClick={() => shift(-1)} className="h-10 w-10 border border-stone-300 flex items-center justify-center bg-white"><ChevronLeft size={16} /></button>
          <div className="font-bold" data-testid="ilog-week-label">{t("il.week_of")} {weekLabel(week)}</div>
          <button data-testid="ilog-week-next" onClick={() => shift(1)} className="h-10 w-10 border border-stone-300 flex items-center justify-center bg-white"><ChevronRight size={16} /></button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((r) => {
          const done = r.actual_hours >= r.hours;
          return (
            <div key={r.id} data-testid={`ilog-card-${r.block_id}`} className={`bg-white border p-4 space-y-3 ${done ? "border-[#7C9673]" : "border-stone-200"}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{r.block_label}</div>
                  <div className="text-xs text-stone-500">{r.ranch_number} · {r.vineyard_name}</div>
                  <div className="text-xs text-sky-800 font-semibold">{r.gallons_per_vine ? `${r.gallons_per_vine} gal/vine · ` : ""}{Number(r.gallons).toLocaleString()} {t("il.gal_total")}</div>
                </div>
                <Droplets size={20} className={done ? "text-[#7C9673]" : "text-sky-500"} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-stone-50 border border-stone-200 p-2">
                  <div className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{r.hours}</div>
                  <div className="text-[9px] font-bold uppercase text-stone-500">{t("il.total_hours")}</div>
                </div>
                <div className="bg-stone-50 border border-stone-200 p-2">
                  <div className="text-2xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{r.sets ?? "—"}</div>
                  <div className="text-[9px] font-bold uppercase text-stone-500">{t("il.sets")} × {r.hours_per_set || "?"}h</div>
                </div>
                <div className="bg-stone-50 border border-stone-200 p-2">
                  <div className={`text-2xl font-bold ${done ? "text-[#1F3D34]" : ""}`} style={{ fontFamily: "'Fraunces', serif" }}>{r.actual_hours}</div>
                  <div className="text-[9px] font-bold uppercase text-stone-500">{t("il.done")}</div>
                </div>
              </div>
              {r.note && <div className="text-xs text-stone-600 bg-amber-50 border border-amber-200 p-2">{r.note}</div>}
              <button data-testid={`ilog-open-${r.block_id}`} onClick={() => openLog(r)}
                className="w-full h-12 bg-[#1F3D34] text-white font-bold uppercase text-sm">{t("il.log")}</button>
            </div>
          );
        })}
        {rows.length === 0 && (
          <div className="col-span-full bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="ilog-empty">
            {t("il.none")}
          </div>
        )}
      </div>

      {logFor && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-sm p-5 space-y-4" data-testid="ilog-modal">
            <div className="font-bold text-lg" style={{ fontFamily: "'Fraunces', serif" }}>{logFor.block_label} — {t("il.log")}</div>
            <div>
              <div className="text-[10px] font-bold uppercase text-stone-500 mb-1">{t("il.date")}</div>
              <input data-testid="ilog-date-input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="w-full h-12 px-3 border border-stone-300" />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase text-stone-500 mb-1">{t("il.hours")}</div>
              <input data-testid="ilog-hours-input" type="number" step="0.5" min="0" inputMode="decimal" value={f.hours} onChange={(e) => setF({ ...f, hours: e.target.value })}
                className="w-full h-14 px-3 border border-stone-300 text-2xl font-bold text-center" placeholder="0" />
            </div>
            <input data-testid="ilog-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="w-full h-11 px-3 border border-stone-300 text-sm" placeholder={t("il.note")} />
            {!navigator.onLine && <div className="text-xs text-amber-700 font-semibold flex items-center gap-1"><CloudOff size={13} /> {t("il.offline")}</div>}
            <div className="flex gap-2">
              <button data-testid="ilog-cancel" onClick={() => setLogFor(null)} className="flex-1 h-12 border border-stone-300 font-bold uppercase text-xs">{t("il.cancel")}</button>
              <button data-testid="ilog-submit" onClick={submit} disabled={busy || !f.hours}
                className="flex-1 h-12 bg-[#1F3D34] text-white font-bold uppercase text-xs disabled:opacity-40">{t("il.save")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
